import { ModalWindowType } from "@multiweb/reactCore/bridge";
import { IRunMethodResult } from "@multiweb/types/contracts";
import { OWObject } from "OW.Remoting/Remoting/DataTypes/OWObject";
import { ErrorHandler } from "UI/Errors";
import { IRunWithoutEdit } from "UI/UI.BaseControls/ControlInterfaces";
import { DIContainer } from "../../../../../Global/Base/DIContainer";
import { InvalidOperationException } from "../../../../../Global/Base/Exceptions";
import { OWObjectList } from "../../../../../OW.Remoting/Remoting/DataTypes/OWObjectList";
import {
    IWFOpenFormEventArgs,
    IWFOpenFormParams,
    IWorkflowManager,
    IWorkflowMenuItem,
} from "../../../../../OW.Remoting/Remoting/IWorkflowManager";
import { OWManager } from "../../../../../OW.Remoting/Remoting/OWManager";
import { IActionContext, MenuType, SelectionType } from "../../../../UI.Controls/Actions/IAction";
import { ControlParams } from "../../Controls/ControlInterfaces";
import { IFRMParams } from "../../Controls/FormParameters";
import { FormManager } from "../../FormManager/IFormManager";
import { UIManager } from "../../UIManager";
import { ActionSharedMethods } from "./ActionSharedMethods";
import { WFActionBase } from "./WFActionBase";

/**
 * Tato WF akce se nevytváří dynamicky jako klasická WFAction, ale podobně jako MethodAction se získává z XG definice formuláře.
 *
 * Měla by se používat jen u framů, které nepoužívají instance objektů (např.: LiteListFrame), jelikož se musí spouštět
 * pouze s parametrem **context** (ten je typu **IActionContext** a vytváří se například v MultiObjectAction v metodě execute).
 */
export class WFStaticAction extends WFActionBase implements IRunWithoutEdit {
    public actionID: string;
    /**
     * Handler události pro otevření formuláře z WF
     */
    private _wfOpenFormListener: (sender: any, message: IWFOpenFormEventArgs) => void;

    public readonly codeName: string;
    /**
     * Příznak, zda se po vykonání metody má obnovit seznam
     */
    public readonly refreshList: boolean;

    private _runWithoutEdit: boolean;

    public get runWithoutEdit(): boolean {
        return this._runWithoutEdit;
    }

    public override get supportedSelectionModes(): SelectionType {
        return SelectionType.all;
    }
    protected readonly _workflowManager: IWorkflowManager;

    constructor(params: ControlParams) {
        super(params);
        this.element.classList.add("WFAction");
        this.actionID = this.element.getAttribute("data-actionid");
        this.refreshList = this.getBoolDataAttr("refreshlist");
        this.validatePostReq = this.getBoolDataAttr("validatepostreq");
        this.validateRecommended = this.getBoolDataAttr("validaterecommended");
        this._runWithoutEdit = this.getBoolDataAttr("runwithoutedit", false);
        this.codeName = this.element.getAttribute("data-codename");

        this._workflowManager = DIContainer.instance.create(IWorkflowManager);
    }

    protected override async _internalExecute(context: IActionContext): Promise<void> {
        // zkontroluje se uložení změn na owneru a jeho nadřízených framech
        if (this.owner && !(await this._checkAndSaveForm()))
            return null;

        if (!(await this._checkRights(this.validateRecommended, this.validatePostReq, "GUI-51363"))) { //je potřebna kontrola vyplnění odložené povinných a/nebo doporučených polí, "GUI-51363" - spustit
            return null;
        }
        await this._confirmedExecuteWithoutInstance(context);
    }

    /** Spuštění pro ID objektu (nesmí používat instanci z framu) */
    protected async _confirmedExecuteWithoutInstance(context: IActionContext): Promise<void> {
        try {
            if (this._wfOpenFormListener) {
                this._workflowManager.openFormReceived.remove(this._wfOpenFormListener);
            }
            if (!context) {
                // Důvod výjimky je vysvětlen v popisku této třídy
                throw new InvalidOperationException("WFAction from form definition can be execute only with parameter context.");
            }
            const { className, ids, isSelectedAll, condition, conditionParams } = context;

            // Pokud je context.isSelectedAll === true, všechny záznamy se považují za vybrané, takže není potřeba mít alespoň jedno ID záznamu.
            if (!context.className || (context.isSelectedAll ? false : !context.ids || context.ids.length === 0))
                throw new InvalidOperationException("Cannot execute WFAction. ID or className must be specified when invoking action for object without instance.");

            UIManager.instance.showLoader();

            let result: IRunMethodResult;

            if (!isSelectedAll && ids.length === 1) {
                this._wfOpenFormListener = (_sender, openFormEventArgs) => this._wfOpenFormHandler(openFormEventArgs);
                this._workflowManager.openFormReceived.add(this._wfOpenFormListener);

                if (this.codeName) {
                    let object: OWObject;
                    try {
                        object = await OWManager.getObject(ids[0], className, undefined, undefined, undefined, this._getRequestContext());

                        let items: IWorkflowMenuItem[] = null;
                        const wfItemsResult = await this._workflowManager.getWFItems(object, true, this._getRequestContext()).catch((err) => {
                            ErrorHandler.instance.handleError(err);
                            return null;
                        });
                        if (wfItemsResult != null) {
                            items = wfItemsResult.workflowMenuItems;
                        }

                        const action = items.find(x => x.Codename == this.codeName);
                        if (action) {
                            let participants = undefined;
                            const { ParticipantsSelecting: isParticipantSelecting, ChooseFromUsersDefault: chooseFromUsersDefault } = action ?? {};

                            if (isParticipantSelecting) {
                                try {
                                    UIManager.instance.hideLoader();
                                    participants = await this._handleParticipantsWindow(object.instanceID, null, action.NextActivityID, chooseFromUsersDefault);
                                } catch (e) {
                                    ErrorHandler.instance.handleError(e);
                                    return;
                                }
                            }

                            await this._finishExecution(action, object, false, participants);
                        }
                    } finally {
                        object?.dispose();
                        this._workflowManager.openFormReceived.remove(this._wfOpenFormListener);
                    }

                    return;
                } else {
                    result = await OWManager.objectWFActionExecuteWithoutInstance(ids, className, this.actionID, null);
                    this._workflowManager.openFormReceived.remove(this._wfOpenFormListener);
                }
            } else if (isSelectedAll || ids.length > 1) {
                const objList = new OWObjectList();
                objList.isSelectAll = isSelectedAll;
                objList.condition = condition;
                objList.conditionParams = conditionParams;
                objList.className = className;
                ids.forEach(id => objList.add({ className, id }));
                // TODO: WI 30151 - Hromadné spuštění WFAkcí pomocí ID objektů
                //result = await OWManager.objectListWFActionExecute(objList, this.actionID, null);
                throw new InvalidOperationException("WFAction has not implemented execute method for more than one object.");
            }
            await ActionSharedMethods.checkMethodResult(result, this.locKey);

            UIManager.instance.showResultMessages(result.messages);
        } catch (e) {
            ActionSharedMethods.handleErrors(e, this.owner.actualObject, this.owner);
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    private _wfOpenFormHandler(args: IWFOpenFormEventArgs): void {
        const message = args.message;

        if (!args.handled && message && message.message && message.message.body) { // pouze pro EditableDBDataSource aneb ne pro OLE
            // TODO: Prozatím se z jednoho požadavku(jedna instance objektu) na otevření formulářů z WF zpracuje pouze první formulář. Chování při zpracování více zatím nebylo vymyšleno.
            // Pro úpravu odkomentovat následující for a upravit podle níže uvedených TODO; Bez dalších úprav by ale formuláře jen "naplácalo" na sebe
            //for (let item of message.Message.Body) {
            const body = message.message.body as IWFOpenFormParams[];
            const item: IWFOpenFormParams = body[0]; // TODO: viz TODO výše - odstranit tento řádek
            if (item == null || !(item instanceof IWFOpenFormParams)) {
                //this._logger.log(`WFOpenFormDataEvent: Item has already been read (removed) or is not in correct format.`); // `
                return;
            }

            const formParameters: IFRMParams = {
                viewMode: false,
                inLOVMode: false,
                isModal: true,
                applyLOVCondition: false,
                autoSelectFirstRow: false,
                openInEdit: false,
                ownerForm: this.ownerForm,
                executerFrame: this.owner,
                formContext: this.owner?.frameContext,
                transactionContextGetter: (this._useMasterContext || this.enableInEdit) ? this.owner?.frameContext?.getTransactionContextId : null,
            };

            switch (item.Purpose) {
                case 1:
                    formParameters.purposeType = MenuType.mtPrint;
                    break;
                case 2:
                    formParameters.purposeType = MenuType.mtActions;
                    break;
                default:
            }

            if (item.Condition)
                formParameters.condition = item.Condition;

            FormManager.instance.openFormModal({ formName: item.FormName, parametricData: formParameters, type: ModalWindowType.parametricForm, showBreadcrumbNav: true, parentFormId: this.ownerForm.formInstanceId });
        }
    }
}
